import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import api from "../lib/api";
import { fmtMoney, fmtDate } from "../lib/format";
import DataTable from "../components/DataTable";
import StatusPill from "../components/StatusPill";
import EntityFormDialog from "../components/EntityFormDialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";

export default function TradeFinance() {
  const { data: lcs = [] } = useQuery({ queryKey: ["lcs"], queryFn: () => api.get("/letters-of-credit") });
  const { data: facilities = [] } = useQuery({ queryKey: ["facilities"], queryFn: () => api.get("/bank-facilities") });
  const { data: banks = [] } = useQuery({ queryKey: ["banks"], queryFn: () => api.get("/banks") });
  const [lcForm, setLcForm] = useState(false);

  const lcFields = [
    { key: "lc_number", label: "LC Number", type: "text", required: true },
    { key: "type", label: "Type", type: "select", options: ["Sight", "Usance"], default: "Sight", required: true },
    { key: "amount", label: "Amount", type: "number", required: true },
    { key: "currency", label: "Currency", type: "select", options: ["USD", "SGD", "INR"], default: "USD" },
    { key: "issuing_bank", label: "Issuing Bank", type: "text" },
    { key: "advising_bank", label: "Advising Bank", type: "text" },
    { key: "expiry_date", label: "Expiry Date", type: "date", required: true },
    { key: "latest_shipment", label: "Latest Shipment", type: "date" },
    { key: "presentation_status", label: "Documents", type: "select", options: ["Documents Pending", "Documents Presented", "Documents Accepted"], default: "Documents Pending" },
    { key: "status", label: "Status", type: "select", options: ["Active", "Closed"], default: "Active" },
    { key: "discrepancies", label: "Discrepancies", type: "number", default: 0 },
  ];

  const daysTo = (d) => {
    try { return Math.round((new Date(d) - new Date()) / 86400000); } catch { return null; }
  };

  const lcCols = [
    { key: "lc_number", label: "LC Number", render: (r) => <span className="font-mono text-xs font-semibold">{r.lc_number}</span> },
    { key: "type", label: "Type" },
    { key: "issuing_bank", label: "Issuing Bank" },
    { key: "amount", label: "Amount", align: "right", render: (r) => fmtMoney(r.amount) },
    { key: "presentation_status", label: "Documents", render: (r) => <span className="text-xs">{r.presentation_status}</span> },
    { key: "expiry_date", label: "Expiry", render: (r) => {
        const d = daysTo(r.expiry_date);
        return <span className={d <= 21 ? "text-rose-600 font-semibold" : ""}>{fmtDate(r.expiry_date)} {d != null && `(${d}d)`}</span>;
      } },
    { key: "discrepancies", label: "Disc.", align: "right", render: (r) => r.discrepancies },
    { key: "status", label: "Status", render: (r) => <StatusPill status={r.status} /> },
  ];

  const facCols = [
    { key: "name", label: "Bank" },
    { key: "facility_limit", label: "Facility Limit", align: "right", render: (r) => fmtMoney(r.facility_limit) },
    { key: "drawn", label: "Drawn", align: "right", render: (r) => fmtMoney(r.drawn) },
    { key: "available", label: "Available", align: "right", render: (r) => <span className="text-emerald-600">{fmtMoney(r.available)}</span> },
    { key: "utilisation_pct", label: "Utilisation", align: "right", render: (r) => (
      <div className="flex items-center gap-2 justify-end">
        <div className="w-20 h-1.5 bg-slate-100 dark:bg-slate-800 rounded-full overflow-hidden">
          <div className={`h-full ${r.utilisation_pct > 85 ? "bg-rose-500" : "bg-orange-500"}`} style={{ width: `${Math.min(r.utilisation_pct, 100)}%` }} />
        </div>
        <span className="font-mono text-xs w-12">{r.utilisation_pct}%</span>
      </div>
    ) },
  ];

  return (
    <div data-testid="trade-finance-page">
      <Tabs defaultValue="lc">
        <TabsList className="mb-3">
          <TabsTrigger value="lc" data-testid="tab-lc">Letters of Credit</TabsTrigger>
          <TabsTrigger value="facilities" data-testid="tab-facilities">Bank Facilities</TabsTrigger>
        </TabsList>
        <TabsContent value="lc">
          <DataTable testid="lc-table" title="Letter of Credit Tracker" columns={lcCols} rows={lcs} csvName="letters_of_credit"
            onAdd={() => setLcForm(true)} addLabel="New LC" />
        </TabsContent>
        <TabsContent value="facilities">
          <DataTable testid="facilities-table" title="Bank Facility Utilisation" columns={facCols} rows={facilities} csvName="bank_facilities" searchable={false} />
        </TabsContent>
      </Tabs>
      <EntityFormDialog open={lcForm} onClose={() => setLcForm(false)} title="New Letter of Credit"
        path="letters-of-credit" fields={lcFields} testid="lc-form" />
    </div>
  );
}
